<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="560px"
    :before-close="handleClose"
    class="export-modal"
    :modal-append-to-body="true"
  >
    <div class="export-modal__content">
      <div class="export-modal__section">
        <div class="export-modal__section-title">
          <i class="ri-checkbox-multiple-line"></i>
          Select Fields to Export
        </div>
        <div class="export-modal__fields">
          <label
            v-for="field in currentFields"
            :key="field.value"
            class="export-modal__field"
            :class="{ 'export-modal__field--checked': field.selected }"
          >
            <input
              type="checkbox"
              v-model="field.selected"
              class="export-modal__checkbox"
            />
            <span class="export-modal__checkmark">
              <i class="ri-check-line"></i>
            </span>
            <span class="export-modal__field-label">{{ field.label }}</span>
          </label>
        </div>
      </div>

      <div v-if="exportType === 'clients'" class="export-modal__section">
        <div class="export-modal__section-title">
          <i class="ri-filter-3-line"></i>
          Filters (Optional)
        </div>
        <div class="export-modal__filters">
          <div class="export-modal__filter">
            <div class="export-modal__filter-info">
              <span class="export-modal__filter-label">Active Clients Only</span>
              <span class="export-modal__filter-desc">Only export records marked as active</span>
            </div>
            <el-switch v-model="filters.isActive" active-color="#0290FC" />
          </div>
          <div class="export-modal__filter">
            <div class="export-modal__filter-info">
              <span class="export-modal__filter-label">Within Operative Area Only</span>
              <span class="export-modal__filter-desc">Exclude records outside service area</span>
            </div>
            <el-switch v-model="filters.withinOperativeArea" active-color="#0290FC" />
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="export-modal__footer">
      <button class="export-modal__btn export-modal__btn--cancel" @click="handleClose">
        Cancel
      </button>
      <button
        class="export-modal__btn export-modal__btn--primary"
        :disabled="isExporting || selectedFields.length === 0"
        @click="handleExport"
      >
        <i v-if="isExporting" class="ri-loader-4-line export-modal__spinner"></i>
        <i v-else class="ri-download-2-line"></i>
        {{ isExporting ? 'Exporting...' : 'Export CSV' }}
      </button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ExportModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    exportType: {
      type: String,
      default: 'customers',
      validator: (value) => ['customers', 'clients'].includes(value)
    }
  },
  data() {
    return {
      isExporting: false,
      customerFields: [
        { value: 'businessName', label: 'Business Name', selected: true },
        { value: 'contactEmail', label: 'Contact Email', selected: true },
        { value: 'firstName', label: 'First Name', selected: true },
        { value: 'lastName', label: 'Last Name', selected: true },
        { value: 'nickName', label: 'Nick Name', selected: false },
        { value: 'middleInitial', label: 'Middle Initial', selected: false },
        { value: 'cellPhone', label: 'Cell Phone', selected: true },
        { value: 'homePhone', label: 'Home Phone', selected: false },
        { value: 'businessPhone', label: 'Business Phone', selected: false },
        { value: 'accountingEmail', label: 'Accounting Email', selected: false },
        { value: 'address', label: 'Address', selected: true },
        { value: 'city', label: 'City', selected: true },
        { value: 'province', label: 'Province', selected: true },
        { value: 'postalCode', label: 'Postal Code', selected: true },
        { value: 'country', label: 'Country', selected: false },
        { value: 'northSouth', label: 'North/South', selected: false }
      ],
      clientFields: [
        { value: 'businessName', label: 'Business Name', selected: true },
        { value: 'firstName', label: 'First Name', selected: true },
        { value: 'lastName', label: 'Last Name', selected: true },
        { value: 'address', label: 'Address', selected: true },
        { value: 'city', label: 'City', selected: true },
        { value: 'province', label: 'Province', selected: true },
        { value: 'postalCode', label: 'Postal Code', selected: true },
        { value: 'cellPhone', label: 'Cell Phone', selected: true },
        { value: 'contactEmail', label: 'Email', selected: true },
        { value: 'notes', label: 'Notes', selected: false }
      ],
      filters: {
        isActive: false,
        withinOperativeArea: false
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    dialogTitle() {
      return this.exportType === 'clients' ? 'Export Public Clients to CSV' : 'Export Customers to CSV'
    },
    currentFields() {
      return this.exportType === 'clients' ? this.clientFields : this.customerFields
    },
    selectedFields() {
      return this.currentFields.filter(f => f.selected).map(f => f.value)
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.filters.isActive = false
        this.filters.withinOperativeArea = false
      }
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:visible', false)
    },
    async handleExport() {
      if (this.selectedFields.length === 0) {
        this.$message.warning('Please select at least one field to export')
        return
      }

      this.isExporting = true
      try {
        const url = process.env.VUE_APP_BACKEND
        const payload = {
          fields: this.selectedFields,
          exportType: this.exportType
        }

        if (this.filters.isActive) {
          payload.isActive = true
        }
        if (this.filters.withinOperativeArea) {
          payload.isOutsideOperativeArea = false
        }

        if (this.exportType === 'clients') {
          payload.isPublic = true
        }

        const response = await axios.post(url + '/customers/export-csv', payload, {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (response.data.type === 'application/json') {
          const text = await response.data.text()
          const errorData = JSON.parse(text)
          throw new Error(errorData.error || 'Export failed')
        }

        const blob = new Blob([response.data], { type: 'text/csv' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        const filename = this.exportType === 'clients' ? 'public_clients' : 'customers'
        link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        window.URL.revokeObjectURL(link.href)

        this.$message.success('Export completed successfully')
        this.handleClose()
      } catch (error) {
        console.error('Export failed:', error)
        let errorMessage = 'Failed to export'
        if (error.response && error.response.data) {
          if (error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text()
              const errorData = JSON.parse(text)
              errorMessage = errorData.error || errorMessage
            } catch (e) {
              console.error('Could not parse error response')
            }
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error
          }
        } else if (error.message) {
          errorMessage = error.message
        }
        this.$message.error(errorMessage)
      } finally {
        this.isExporting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.export-modal {
  ::v-deep .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    margin-top: 10vh !important;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .el-dialog__header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #ebeef5;
  }

  ::v-deep .el-dialog__title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a2e;
  }

  ::v-deep .el-dialog__headerbtn {
    top: 20px;
    right: 20px;
    font-size: 18px;
  }

  ::v-deep .el-dialog__body {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;
  }

  ::v-deep .el-dialog__footer {
    padding: 16px 24px 20px;
    border-top: 1px solid #ebeef5;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 28px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__section-title {
    font-weight: 600;
    font-size: 14px;
    color: #1a1a2e;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #0290FC;
      font-size: 16px;
    }
  }

  &__fields {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  &__field {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #f8f9fc;
    border: 1px solid #e8ebf0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f0f3f8;
      border-color: #d0d5dd;
    }

    &--checked {
      background: rgba(2, 144, 252, 0.08);
      border-color: #0290FC;

      .export-modal__checkmark {
        background: #0290FC;
        border-color: #0290FC;

        i {
          opacity: 1;
        }
      }

      .export-modal__field-label {
        color: #1a1a2e;
      }
    }
  }

  &__checkbox {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__checkmark {
    width: 18px;
    height: 18px;
    border: 2px solid #c4c9d4;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;

    i {
      color: white;
      font-size: 12px;
      opacity: 0;
      transition: opacity 0.2s ease;
    }
  }

  &__field-label {
    font-size: 13px;
    color: #4a5568;
    font-weight: 500;
    transition: color 0.2s ease;
  }

  &__filters {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__filter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    background: #f8f9fc;
    border: 1px solid #e8ebf0;
    border-radius: 10px;
  }

  &__filter-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__filter-label {
    font-size: 14px;
    font-weight: 500;
    color: #1a1a2e;
  }

  &__filter-desc {
    font-size: 12px;
    color: #8492a6;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    i {
      font-size: 16px;
    }

    &--cancel {
      background: #f5f7fa;
      color: #606266;
      border: 1px solid #dcdfe6;

      &:hover {
        background: #e8ebf0;
        border-color: #c4c9d4;
      }
    }

    &--primary {
      background: #0290FC;
      color: white;
      box-shadow: 0 4px 12px rgba(2, 144, 252, 0.3);

      &:hover:not(:disabled) {
        background: #0180e0;
        box-shadow: 0 6px 16px rgba(2, 144, 252, 0.4);
        transform: translateY(-1px);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
    }
  }

  &__spinner {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
